import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { MessageResponse } from "../../../api/generated";
import { useSnackbar } from "../../../components/common/SnackbarProvider";
import { markdownSx } from "./markdownStyles";

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

interface MessageBubbleProps {
  message: MessageResponse;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const { showSnackbar } = useSnackbar();
  const isUser = message.role === "user";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    showSnackbar("Copied to clipboard");
  };

  if (isUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, px: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            maxWidth: { xs: "95%", sm: "75%" },
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2, px: 2 }}>
      {/* Avatar + name + timestamp row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "primary.main",
            fontSize: "0.85rem",
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary"
          }}>
          Sales Coach
        </Typography>
        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          {formatTime(message.created_at)}
        </Typography>
        <Tooltip title="Copy to clipboard">
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label="Copy to clipboard"
            sx={{ ml: "auto" }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Message bubble */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          maxWidth: { xs: "95%", sm: "85%" },
          bgcolor: "grey.100",
          borderRadius: 2,
          ml: 4.5,
        }}
      >
        <Box sx={{ wordBreak: "break-word", ...markdownSx }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  );
}
